export const SITE_URL = "https://www.cmopartner.fr";

/** Search-engine breadcrumb shared by the service detail routes. */
export function serviceBreadcrumb(path: string, name: string, english = false) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: english ? "Home" : "Accueil", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services` },
      { "@type": "ListItem", position: 3, name, item: `${SITE_URL}${path}` },
    ],
  };
}

type PageHeadArgs = {
  path: string;
  title: string;
  description: string;
  /** Explicit FR alternate path (defaults to path with /en stripped). */
  frPath?: string;
  /** Explicit EN alternate path; null disables hreflang tags entirely. */
  enPath?: string | null;
  noindex?: boolean;
  /** Breadcrumb trail after "Accueil"/"Home": [name, path] pairs. Emits BreadcrumbList. */
  crumbs?: [string, string][];
  /** Extra JSON-LD objects rendered server-side. */
  schemas?: Record<string, unknown>[];
};

/**
 * Builds the SSR head for a route: title, description, canonical (always
 * https://www.cmopartner.fr), og tags and fr/en/x-default hreflang alternates.
 */
export function pageHead({ path, title, description, frPath, enPath, noindex, crumbs, schemas = [] }: PageHeadArgs) {
  const fr = frPath ?? (path.startsWith("/en") ? path.replace(/^\/en/, "") || "/" : path);
  const en = enPath === null ? null : (enPath ?? (path.startsWith("/en") ? path : `/en${path === "/" ? "" : path}`));

  const ld = [...schemas];
  if (crumbs) ld.push(breadcrumb(crumbs, path.startsWith("/en")));

  return {
    scripts: ld.map((x) => ({ type: "application/ld+json", children: JSON.stringify(x) })),
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: `${SITE_URL}${path}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      ...(noindex ? [{ name: "robots", content: "noindex, nofollow" }] : []),
    ],
    links: [
      { rel: "canonical", href: `${SITE_URL}${path}` },
      ...(en
        ? [
            { rel: "alternate", hrefLang: "fr", href: `${SITE_URL}${fr}` },
            { rel: "alternate", hrefLang: "en", href: `${SITE_URL}${en}` },
            { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}${fr}` },
          ]
        : []),
    ],
  };
}

export function breadcrumb(crumbs: [string, string][], english = false) {
  const all: [string, string][] = [[english ? "Home" : "Accueil", "/"], ...crumbs];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: all.map(([name, p], i) => ({
      "@type": "ListItem",
      position: i + 1,
      name,
      item: `${SITE_URL}${p}`,
    })),
  };
}

export function faqSchema(faq: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

export const FOUNDER = {
  "@type": "Person",
  name: "Tiphany Rigaudias",
  jobTitle: ["Fractional CMO", "Directrice marketing externalisée", "Directrice digitale externalisée"],
  knowsAbout: ["Marketing B2B", "CRM", "Marketing automation", "IA appliquée au marketing", "Formation des équipes marketing"],
  url: `${SITE_URL}/a-propos`,
  sameAs: ["https://linkedin.com/in/tiphanyrigaudias"],
};

/** Service + FAQPage JSON-LD for pages rendered by LandingPage. */
export function landingSchemas(c: { h1: string; description: string; faq?: { q: string; a: string }[] }, opts?: { serviceType?: string; alternateName?: string[] }) {
  const out: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: c.h1,
      description: c.description,
      ...(opts?.serviceType ? { serviceType: opts.serviceType } : {}),
      ...(opts?.alternateName ? { alternateName: opts.alternateName } : {}),
      provider: { "@type": "Organization", name: "CMO Partner", url: SITE_URL, founder: FOUNDER },
      areaServed: [
        { "@type": "City", name: "Bordeaux" },
        { "@type": "AdministrativeArea", name: "Nouvelle-Aquitaine" },
        { "@type": "Country", name: "France" },
      ],
      availableLanguage: ["fr", "en"],
    },
  ];
  if (c.faq?.length) out.push(faqSchema(c.faq));
  return out;
}
